<template>
  <form v-if="contract" class="client-review-form" @submit.prevent="handleSubmit">
    <div class="client-review-intro">
      <h2>Review client details</h2>
      <p>Confirm the onboarding details before creating the client record.</p>
    </div>
    <section class="client-review-section" aria-labelledby="review-client-heading">
      <h3 id="review-client-heading">Client details</h3>
      <div class="client-review-details">
        <p class="client-review-name">{{ contract?.profile?.name }} <span>({{ contract?.profile?.abbr }})</span></p>
        <p>{{ contract?.profile?.address }}, {{ contract?.profile?.state }}</p>
        <div class="client-review-contact"><span>{{ contract?.profile?.email }}</span><span>{{ contract?.profile?.phone }}</span></div>
      </div>
    </section>
    <section class="client-review-section" aria-labelledby="review-policy-heading">
      <h3 id="review-policy-heading">{{ cat == 'tpa' ? 'TPA' : 'Policy' }} year</h3>
      <div class="client-review-details client-review-policy">
        <span>{{ contract?.policy?.formatted?.withFullMonths }}</span>
        <i v-if="contract.status == 0" class="material-icons" title="Pending" aria-label="Pending">schedule</i>
      </div>
    </section>
    <section class="client-review-section" aria-labelledby="review-settings-heading">
      <h3 id="review-settings-heading">Settings</h3>
      <dl class="client-review-settings">
        <div><dt>Family size</dt><dd>{{ contract?.settings?.[0]?.value }} members</dd></div>
        <div><dt>Principal age limit</dt><dd>{{ contract?.settings?.[1]?.value }} years</dd></div>
        <div><dt>Dependant age limit</dt><dd>{{ contract?.settings?.[2]?.value }} years</dd></div>
      </dl>
    </section>
    <section class="client-review-section" v-if="cat == 'tpa' && contract?.plans?.length" aria-labelledby="review-plans-heading">
      <h3 id="review-plans-heading">Reference plans</h3>
      <div class="client-review-details">{{ contract.plans.length }} plan{{ contract.plans.length > 1 ? 's' : '' }} selected</div>
    </section>
    <section class="client-review-section" aria-labelledby="review-installments-heading">
      <h3 id="review-installments-heading">Payment installments</h3>
      <div class="client-review-installments">
        <span v-for="installment in contract.installments" :key="installment.id">{{ installment.formatted.shortMonthRange }}</span>
        <i v-if="contract.status == 0" class="material-icons" title="Pending" aria-label="Pending">schedule</i>
      </div>
    </section>
    <div class="progress" v-if="isSubmitting" aria-label="Creating client"><div class="indeterminate"></div></div>
    <div class="client-review-actions">
      <button type="button" class="btn client-review-back" :disabled="isSubmitting" @click="handleBack()"><i class="material-icons" aria-hidden="true">arrow_back</i>Back</button>
      <button type="submit" class="btn" :disabled="!contract || !allModules?.length || !allSchemes?.length || isSubmitting"
        :class="{ disabled: !contract || !allModules?.length || !allSchemes?.length || isSubmitting }">
        <i class="material-icons" aria-hidden="true">check_circle</i>{{ isSubmitting ? 'Creating...' : 'Create Client' }}
      </button>
    </div>
  </form>
  <div v-else class="client-review-unavailable" role="status">Unable to load the client details for review.</div>
</template>
<script>
import { computed, onMounted, ref } from "vue";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import { addModules } from "@/composables/cache/modules";

export default {
    name: "ReviewOnboardComponent",
    emits: ["back", "next"],
    props: ["cat", "newClient"],
    setup(props, context) {
        const response = ref(null);
        const isSubmitting = ref(false);
        const allModules = ref([]);
        const allSchemes = ref([]);
        const error = ref(false);

        // Modules and Schemes
        onMounted(() => {
            // get modules
            allModules.value = addModules()
                .filter(x => x.type === "client")
                .map(item => item.to);

            // get schemes
            (async () => {
                const { res } = await getItems("scheme");

                if (res.value) {
                    allSchemes.value = res.value.data.filter(x => x.type === "p") // private schemes only
                        .map(item => item.id);
                } else {
                    error.value = "ERROR: Important data not loaded!";
                }

            })();
        });

        // on
        onMounted(() => {
            // load
            response.value = props.newClient;
            response.value.policy = getPolicyYearDates(response.value.status[0].value);
            response.value.installments = calculateInstallmentPeriods(response.value.status[0].value, 12 / response.value.status[1].value);
        });

        // policy period
        const getPolicyYearDates = (startDateStr) => {
            /**
             * Calculate exact policy year dates from start date (beginning to end of 12-month period)
             * @param {string} startDateStr - Start date in YYYYMMDD format
             * @returns {object} { policyStart, policyEnd, formatted }
             */

            const monthNames = {
                full: ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'],
                short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            };

            // Parse start date
            const year = parseInt(startDateStr.substring(0, 4));
            const month = parseInt(startDateStr.substring(4, 6)) - 1;
            const day = parseInt(startDateStr.substring(6, 8));
            const policyStart = new Date(year, month, day);

            // Calculate end date (12 months later minus 1 day)
            const policyEnd = new Date(policyStart);
            policyEnd.setFullYear(policyEnd.getFullYear() + 1);
            policyEnd.setDate(policyEnd.getDate() - 1);

            // Formatting helpers
            const formatDate = (date) => date.toISOString().split('T')[0];
            const getMonthName = (date, type) => monthNames[type][date.getMonth()];
            const formatDayMonth = (date) => `${date.getDate()} ${getMonthName(date, 'short')}`;

            return {
                policyStart: new Date(policyStart),
                policyEnd: new Date(policyEnd),
                formatted: {
                    // Basic formats
                    fullStart: formatDate(policyStart),
                    fullEnd: formatDate(policyEnd),
                    displayRange: `${formatDate(policyStart)} to ${formatDate(policyEnd)}`,

                    // Month names
                    startMonth: {
                        full: getMonthName(policyStart, 'full'),
                        short: getMonthName(policyStart, 'short')
                    },
                    endMonth: {
                        full: getMonthName(policyEnd, 'full'),
                        short: getMonthName(policyEnd, 'short')
                    },

                    // Enhanced displays
                    withFullMonths: `${getMonthName(policyStart, 'full')} ${policyStart.getDate()}, ${policyStart.getFullYear()} to ${getMonthName(policyEnd, 'full')} ${policyEnd.getDate()}, ${policyEnd.getFullYear()}`,
                    withShortMonths: `${formatDayMonth(policyStart)}, ${policyStart.getFullYear()} to ${formatDayMonth(policyEnd)}, ${policyEnd.getFullYear()}`,
                    yearRange: `${policyStart.getFullYear()}-${policyEnd.getFullYear()}`,
                    shortRange: `${getMonthName(policyStart, 'short')} ${policyStart.getFullYear()} - ${getMonthName(policyEnd, 'short')} ${policyEnd.getFullYear()}`
                },
                daysInPolicy: Math.floor((policyEnd - policyStart) / (1000 * 60 * 60 * 24)) + 1
            };
        }

        // installments
        const calculateInstallmentPeriods = (startDateStr, installmentsPerYear) => {
            /**
             * Calculate exactly 12-month installment periods from the given start date
             * with proper month name formatting
             */

            const monthNames = {
                full: ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'],
                short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            };

            // Validate input
            if (12 % installmentsPerYear !== 0) {
                throw new Error("Installments per year must divide 12 evenly (1, 2, 3, 4, 6, or 12)");
            }

            // Parse start date
            const year = parseInt(startDateStr.substring(0, 4));
            const month = parseInt(startDateStr.substring(4, 6)) - 1;
            const day = parseInt(startDateStr.substring(6, 8));
            const policyStart = new Date(year, month, day);

            // Calculate exactly 12 months later (same day)
            const policyEnd = new Date(policyStart);
            policyEnd.setFullYear(policyEnd.getFullYear() + 1);
            policyEnd.setDate(policyEnd.getDate() - 1); // Last day of 12-month period

            const monthsPerPeriod = 12 / installmentsPerYear;
            const periods = [];
            let currentStart = new Date(policyStart);

            for (let i = 1; i <= installmentsPerYear; i++) {
                const periodEnd = new Date(currentStart);

                if (i === installmentsPerYear) {
                    // Last period ends at policy end date
                    periodEnd.setTime(policyEnd.getTime());
                } else {
                    periodEnd.setMonth(periodEnd.getMonth() + monthsPerPeriod);
                    periodEnd.setDate(periodEnd.getDate() - 1); // Previous day
                }

                // Formatting functions
                const formatDate = (date) => date.toISOString().split('T')[0];
                const getMonthName = (date, type) => monthNames[type][date.getMonth()];
                const formatDayMonth = (date) =>
                    `${date.getDate()} ${getMonthName(date, 'short')}`;

                periods.push({
                    periodNumber: i,
                    startDate: new Date(currentStart),
                    endDate: new Date(periodEnd),
                    formatted: {
                        // Basic formats
                        fullStart: formatDate(currentStart),
                        fullEnd: formatDate(periodEnd),
                        displayRange: `${formatDate(currentStart)} to ${formatDate(periodEnd)}`,

                        // Month names
                        startMonth: {
                            full: getMonthName(currentStart, 'full'),
                            short: getMonthName(currentStart, 'short')
                        },
                        endMonth: {
                            full: getMonthName(periodEnd, 'full'),
                            short: getMonthName(periodEnd, 'short')
                        },

                        // Enhanced displays
                        withFullMonths: `${getMonthName(currentStart, 'full')} ${currentStart.getDate()}, ${currentStart.getFullYear()} to ${getMonthName(periodEnd, 'full')} ${periodEnd.getDate()}, ${periodEnd.getFullYear()}`,
                        withShortMonths: `${formatDayMonth(currentStart)}, ${currentStart.getFullYear()} to ${formatDayMonth(periodEnd)}, ${periodEnd.getFullYear()}`,
                        monthRange: `${getMonthName(currentStart, 'full')} - ${getMonthName(periodEnd, 'full')} ${periodEnd.getFullYear()}`,
                        shortMonthRange: `${getMonthName(currentStart, 'short')}-${getMonthName(periodEnd, 'short')} ${periodEnd.getFullYear()}`,
                    },
                    daysInPeriod: Math.floor((periodEnd - currentStart) / (1000 * 60 * 60 * 24)) + 1
                });

                // Next period starts the day after this one ends
                currentStart = new Date(periodEnd);
                currentStart.setDate(currentStart.getDate() + 1);
            }

            return periods;
        }

        // handle back
        const handleBack = () => {
            // emit new parameters
            context.emit("back", {
                id: "review",
                value: true,
            });
        }

        // handle back
        const handleSubmit = async () => {
            // perform submission
            // start
            isSubmitting.value = true;

            let newVal = { ...props.newClient, 
                schemes: allSchemes.value,
                modules: allModules.value,
             };

            // post item
            const { res, err } = await postItem("operator/client/add", newVal);
            if (res.value) {
                // emit new parameters
                context.emit("next", {
                    id: "review",
                    value: res.value.data,
                });
            } else {
                error.value = err.value;
            }

            // close
            isSubmitting.value = false;
        }

        // output pages
        const contract = computed(() => {
            if (!response.value) {
                return [];
            } else {
                return response.value;
            }
        });

        return {
            isSubmitting,
            handleSubmit,
            handleBack,
            allModules,
            allSchemes,
            error,
            contract,
        };
    },
};
</script>


<style scoped>
.client-review-form { max-width: 70rem; width: 100%; }
.client-review-intro { margin-bottom: 1.5rem; }
.client-review-intro h2 { color: var(--ui-foreground); font-size: 1.125rem; font-weight: 600; margin: 0 0 .5rem; }
.client-review-intro p { color: var(--ui-muted-foreground); font-size: .8125rem; line-height: 1.6; margin: 0; }
.client-review-section { align-items: start; border-bottom: 1px solid var(--ui-border); display: grid; gap: 2.5rem; grid-template-columns: 13rem minmax(0, 1fr); padding: 1.5rem 0; }
.client-review-section h3 { color: var(--ui-foreground); font-size: .875rem; font-weight: 600; line-height: 1.6; margin: 0; }
.client-review-details { color: var(--ui-foreground); font-size: .875rem; line-height: 1.6; min-width: 0; overflow-wrap: anywhere; }
.client-review-details p { margin: 0 0 .5rem; }
.client-review-name { font-weight: 600; }
.client-review-name span { color: var(--ui-muted-foreground); font-weight: 400; }
.client-review-contact { color: var(--ui-muted-foreground); display: flex; flex-wrap: wrap; font-size: .8125rem; gap: .5rem 1.5rem; }
.client-review-policy { align-items: center; display: flex; gap: .75rem; }
.client-review-settings { display: grid; gap: 1rem; grid-template-columns: repeat(3, minmax(0, 1fr)); margin: 0; }
.client-review-settings dt { color: var(--ui-muted-foreground); font-size: .75rem; line-height: 1.6; margin-bottom: .35rem; }
.client-review-settings dd { color: var(--ui-foreground); font-size: .875rem; font-weight: 500; margin: 0; }
.client-review-installments { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; }
.client-review-installments > span { background: var(--ui-secondary); border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 3px); color: var(--ui-secondary-foreground); font-size: .8125rem; padding: .35rem .65rem; }
.client-review-section i { color: var(--ui-muted-foreground); font-size: 1rem; }
.client-review-actions { align-items: center; display: flex; flex-wrap: wrap; gap: .75rem; justify-content: space-between; padding-top: 1.25rem; }
.client-review-actions button { font-size: .8125rem; font-weight: 600; gap: .5rem; }
.client-review-actions i { font-size: 1rem; }
.client-review-actions .client-review-back { background: var(--ui-background); border: 1px solid var(--ui-border); color: var(--ui-foreground); }
.client-review-unavailable { color: var(--ui-muted-foreground); padding: 1.5rem 0; }
@media (max-width: 900px) { .client-review-section { gap: .75rem; grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 600px) { .client-review-settings { grid-template-columns: minmax(0, 1fr); } }
.review-heading p {
    color: #607d8b;
    font-size: 0.92rem;
    margin: 8px 0 0;
}

.review-panel {
    background: linear-gradient(90deg, #ffffff 0%, #fbfdfd 100%) !important;
    border-color: #d8e4e7 !important;
}

.review-panel .row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0;
}

.review-detail-row {
    color: #607d8b;
    font-size: 0.92rem;
    margin-top: 8px;
}

.review-label {
    color: #455a64;
    display: inline-block;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.review-panel h6 {
    color: #263238;
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0;
}

.review-panel h6 span {
    color: #78909c;
    font-size: 0.88rem;
    font-weight: 700;
}

.review-main-text {
    color: #263238;
    font-weight: 650;
    line-height: 1.4;
}

.review-check {
    color: #00897b;
    font-size: 22px;
}

.review-soft-pill,
.review-setting-pill,
.review-plan-pill,
.review-installment-pill {
    border-radius: 999px;
    display: inline-flex;
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.2;
    margin: 4px 6px 0 0;
    padding: 6px 10px;
}

.review-soft-pill {
    background: #eef5f6;
    color: #455a64;
}

.review-setting-pill {
    background: #e0f2f1;
    color: #00695c;
}

.review-plan-pill {
    background: #e8f0fe;
    border: 1px solid #c7d7fb;
    color: #1a4f8b;
}

.summary-action-row {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-top: 14px;
}

.summary-action-row .btn-large {
    border-radius: 5px;
    min-width: 138px;
}

.review-installment-pill {
    background: #fff8e1;
    border: 1px solid #ffe082;
    color: #6d4c41;
}

.review-chip-line {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
}

.progress {
    margin-top: -15px;
}

.spacer10 {
    margin-top: 10px;
    margin-bottom: 10px;
}

.pointer {
    cursor: default;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
    color: rgb(21, 21, 206) !important;
}

/* open */
.chip-more {
    cursor: pointer;
    background-color: #eaecee !important;
}

.chip-more:hover {
    color: white !important;
    background-color: black !important;
}

/* close */
.chip-less {
    cursor: pointer;
    color: white !important;
    background-color: black !important;
}

.chip-less:hover {
    color: black !important;
    background-color: #eaecee !important;
}

/* base class */
.tab-custom {
    display: inline-block;
    cursor: pointer;
    margin: 0 0 -1px;
    padding: 10px 15px;
    font-weight: 600;
    text-align: center;
    border: 1px solid transparent;
}

.tab-custom:hover {
    color: #000000 !important;
}

.tab-custom-active {
    display: inline-block;
    cursor: pointer;
    margin: 0 0 -1px;
    padding: 10px 15px;
    color: white;
    font-weight: 600;
    text-align: center;
    background-color: black;
}

/* display */
.tab-custom-section {
    padding: 20px 0 0;
    border-top: 1px solid #ddd;
}

@media only screen and (max-width: 600px) {
    .review-panel .row {
        display: block;
    }

    .review-label {
        margin-bottom: 8px;
    }

    .review-check {
        margin-top: 10px;
    }

    .summary-action-row {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
